import { FC, ReactNode } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import { TYPE_ERROR, TYPE_INFO, TYPE_WARNING } from '../../store/notifications/actions';
import { borderColor, textColor, THEME_PRIMARY, THEME_RED, THEME_WARNING } from '../../styles/basics/colors';
import { SysaElement } from '../../store/notifications/state';

const StNotification = styled.div<NotificationStyledProps>`
    display: flex;
    background-color: #fff;
    align-items: flex-start;
    min-width: 150px;
    ${props =>
        borderColor(
            props.type === TYPE_ERROR ? THEME_RED : props.type === TYPE_WARNING ? THEME_WARNING : THEME_PRIMARY
        )};
    border-style: solid;
    border-width: 1px;
    padding: 5px 7px;
    border-radius: 2px;
    box-shadow: 0 2px 7px rgba(0, 0, 0, 0.1);
    margin: 2px 2px 10px;
`;

interface NotificationStyledProps {
    type: string;
}

const StNotificationText = styled.span<NotificationStyledProps>`
    margin-right: ${props => (props.type === TYPE_ERROR ? null : '20px')};
    flex-grow: 1;
    line-height: 18px;
`;

const StNotificationIcon = styled.i<NotificationStyledProps>`
    ${props =>
        textColor(props.type === TYPE_ERROR ? THEME_RED : props.type === TYPE_WARNING ? THEME_WARNING : THEME_PRIMARY)};
    margin-right: 5px;
    font-size: 18px;
    line-height: 1 !important;
    flex-shrink: 0;
`;

const StCloseIcon = styled.i`
    margin-left: 5px;
    font-size: 18px;
    line-height: 1 !important;
    cursor: pointer;
    flex-shrink: 0;
`;

interface Props {
    readonly notifications: ReadonlyArray<SysaElement>;
    readonly onClose: (id: number) => void;
}

const Notification: FC<Props> = props => {
    const { notifications, onClose } = props;
    const classes = classNames('sysa', { active: notifications.length > 0 });
    return (
        <div className={classes} data-displayname="Notification">
            {notifications.map(notification => {
                let closeButton: ReactNode;

                if (notification.type === TYPE_ERROR) {
                    closeButton = (
                        <StCloseIcon
                            title="close"
                            className="material-icons"
                            onClick={() => onClose(notification.id ?? 0)}
                        >
                            close
                        </StCloseIcon>
                    );
                }
                let iconCode;
                switch (notification.type) {
                    case TYPE_WARNING:
                        iconCode = 'error_outline';
                        break;
                    case TYPE_ERROR:
                        iconCode = 'block';
                        break;
                    case TYPE_INFO:
                        iconCode = 'check';
                        break;
                    default:
                        iconCode = null;
                        break;
                }
                const icon = (
                    <StNotificationIcon className="material-icons" type={notification.type ?? ''}>
                        {iconCode}
                    </StNotificationIcon>
                );
                return (
                    <StNotification
                        key={notification.id}
                        type={notification.type ?? ''}
                        data-testid="notification-info"
                    >
                        {icon}
                        <StNotificationText
                            dangerouslySetInnerHTML={{ __html: notification.text ?? '' }}
                            type={notification.type ?? ''}
                        />
                        {closeButton}
                    </StNotification>
                );
            })}
        </div>
    );
};

export default Notification;
